import { PRIORITY_LEVELS } from "./tasks";

// Tasks Kanban: one column per STORED status. "En retard" is never a column (it is derived from the due date, see
// lib/tasks.js), it shows as a badge on the card. Pure functions, unit-tested in kanban.test.js.
export const KANBAN_COLUMNS = [
  { id: "a_faire", label: "À faire", dot: "bg-slate-400", head: "bg-slate-100 text-slate-700" },
  { id: "en_cours", label: "En cours", dot: "bg-blue-500", head: "bg-blue-50 text-blue-700" },
  { id: "termine", label: "Terminées", dot: "bg-emerald-500", head: "bg-emerald-50 text-emerald-700" },
  { id: "annulee", label: "Annulées", dot: "bg-slate-300", head: "bg-slate-200 text-slate-600" },
];

export const KANBAN_SORTS = [
  { id: "due", label: "Échéance" },
  { id: "priority", label: "Priorité" },
  { id: "recent", label: "Plus récentes" },
];

// Columns that accept a "+" quick add: a task is always created "À faire" (the API never accepts a status at creation),
// so only those two can be reached without a reason or a completion comment.
export const KANBAN_QUICK_ADD = ["a_faire", "en_cours"];

export function taskColumnId(task) {
  return task.status || (task.done ? "termine" : "a_faire");
}

const PRIORITY_RANK = Object.fromEntries([...PRIORITY_LEVELS].reverse().map((p, i) => [p.id, i]));

function byDue(a, b) {
  if (!a.due_date && !b.due_date) return 0;
  if (!a.due_date) return 1;
  if (!b.due_date) return -1;
  return a.due_date < b.due_date ? -1 : a.due_date > b.due_date ? 1 : 0;
}

const COMPARATORS = {
  due: byDue,
  priority: (a, b) => (PRIORITY_RANK[a.priority] ?? 99) - (PRIORITY_RANK[b.priority] ?? 99) || byDue(a, b),
  recent: (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")),
};

export function sortKanbanTasks(tasks, sortId = "due") {
  return [...tasks].sort(COMPARATORS[sortId] || COMPARATORS.due);
}

// `overrides` = {taskId: columnId}: where the card is SHOWN while a move is being saved or waits for its dialog
// (optimistic move), without touching the task itself.
export function groupTasksByColumn(tasks, sortId = "due", overrides = {}) {
  const groups = Object.fromEntries(KANBAN_COLUMNS.map((c) => [c.id, []]));
  for (const task of tasks) {
    const column = overrides[task.id] || taskColumnId(task);
    (groups[column] || groups.a_faire).push(task);
  }
  for (const column of KANBAN_COLUMNS) groups[column.id] = sortKanbanTasks(groups[column.id], sortId);
  return groups;
}

// What dropping a card into `target` requires: closing asks for the optional completion comment, cancelling for a reason
// (the API refuses a cancellation without one), everything else is a plain status change.
export function moveRequirement(from, target) {
  if (!target || from === target) return "none";
  if (target === "termine") return "complete";
  if (target === "annulee") return "cancel";
  return "direct";
}
